import { useState, useEffect } from "react";
import { Flex, Box, Text, useColorModeValue } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useControls } from "src/utils/createControls";
import { Monitoring } from "src/modules/activity-log/monitoring-info";
import { getAppValue } from "src/utils/environment";
import Dialog from "src/components/Dialog";
import IconAsButton from "src/components/IconAsButton";
import SvgContainer from "src/components/SvgContainer";
import { ReactComponent as ShareIcon } from "src/icons/share-people.svg";
import { ReactComponent as CheckIcon } from "src/icons/check-mark.svg";
import { ReactComponent as CopyIcon } from "src/icons/copy.svg";
import time from "src/utils/time";

export default function MonitoringShare({ toggleShare, monitoring }) {
  const [hasStoppedSharing, setHasStoppedSharing] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const dialog = useControls();

  const isShared = Monitoring.isShared(monitoring);
  const shareKey = monitoring.shareableKey;
  const sharedUntil = monitoring.shareableUntil?.iso;

  const buttonText = hasStoppedSharing ? "buttons.ok" : "buttons.stopSharing";

  const title = hasStoppedSharing
    ? "activityLog.sharing.stopped.heading"
    : "activityLog.sharing.heading";
  const subtitle = hasStoppedSharing
    ? t("activityLog.sharing.stopped.text")
    : t("activityLog.sharedUntil", {
        value0: time.displayDate(sharedUntil, {
          day: "numeric",
          month: "long",
          year: "numeric"
        })
      });

  const color = useColorModeValue(
    "primary",
    getAppValue({
      barkio: "#6eb918",
      bibino: "rgba(154, 124, 246, 1)"
    })
  );

  useEffect(() => {
    async function shareAL() {
      if (isShared) return;
      setLoading(true);
      await toggleShare();
      setLoading(false);
    }
    if (dialog.isOpen) {
      setHasStoppedSharing(false);
      shareAL();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog.isOpen]);

  async function stopShare() {
    setLoading(true);
    await toggleShare();
    setLoading(false);
    setHasStoppedSharing(true);
  }

  async function copyLink() {
    if (!shareKey) return;
    await navigator.clipboard.writeText(
      `https://${getAppValue({
        bibino: "bibinoapp",
        barkio: "barkio"
      })}.com/activitylog/${shareKey}`
    );
  }

  return (
    <>
      <Dialog
        isOpen={dialog.isOpen}
        heading={t(title)}
        text={subtitle}
        buttons={[
          !hasStoppedSharing && {
            text: t(buttonText),
            onClick: stopShare,
            css: { color: "warningBase", _hover: { bg: "warningLight" } }
          },
          {
            text: t("buttons.ok"),
            onClick: dialog.close
          }
        ]}
        isProcessing={loading}
        additionalContent={
          !hasStoppedSharing && (
            <Flex
              px="24px"
              mt="28px"
              bg="primaryLight"
              justifyContent="space-between"
              alignItems="center"
              borderRadius="32px"
              _hover={{
                ".keyscroll": {
                  overflow: "auto",
                  textOverflow: "unset"
                }
              }}
              sx={{
                ".keyscroll::-webkit-scrollbar": {
                  appearance: "none",
                  display: "none"
                },
                ".keyscroll": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }
              }}
            >
              <>
                <Text
                  py="18px"
                  className="keyscroll"
                  color={color}
                  fontSize="1.4rem"
                  lineHeight="1.14"
                  letterSpacing="0.15px"
                  sx={{
                    userSelect: "all"
                  }}
                >
                  {`https://${getAppValue({
                    bibino: "bibinoapp",
                    barkio: "barkio"
                  })}.com/activitylog/${shareKey}`}
                </Text>
                <Box
                  ml="16px"
                  p="4px"
                  position="relative"
                  transition="all 0.3s"
                  borderRadius="100%"
                  _hover={{ bg: "primaryLight" }}
                  _active={{ bg: "transparent" }}
                  onClick={copyLink}
                >
                  <SvgContainer icon={CopyIcon} fill="primary" size="20px" />
                </Box>
              </>
            </Flex>
          )
        }
      />

      <Box onClick={dialog.open} position="relative">
        {isShared && (
          <Box
            position="absolute"
            top="0%"
            right="0%"
            transform="translate(25%, 0%)"
          >
            <SvgContainer
              icon={CheckIcon}
              size="12px"
              fill={getAppValue({
                bibino: "secondary",
                barkio: "primary"
              })}
            />
          </Box>
        )}
        <IconAsButton icon={ShareIcon} size="20px" fill="mediumEmphasis" />
      </Box>
    </>
  );
}
